import type { AssetQuickLaunchPrefill } from 'components/editorial/AssetQuickLaunch/types';
import type { BloxCustomDataProviderType, PatchType } from 'utility/types/dataProvider';

/**
 * Builds JSON patch operations from quick launch prefill values.
 * Shared utility for both asset and revision duplication workflows.
 */
export const buildQuickLaunchPatch = (prefill?: AssetQuickLaunchPrefill): PatchType[] => {
  if (!prefill) {
    return [];
  }

  const patches: PatchType[] = [];

  if (prefill.workflowId !== undefined) {
    const workflowValueRaw = prefill.workflowId;
    const normalizedWorkflow =
      typeof workflowValueRaw === 'string' ? workflowValueRaw.trim() : workflowValueRaw;

    if (typeof normalizedWorkflow === 'string' && normalizedWorkflow.length > 0) {
      patches.push({
        path: '/properties/workflow_id',
        op: 'add',
        value: normalizedWorkflow as unknown as string,
      });
    }
  }

  if (prefill.startTime !== undefined) {
    const startValueRaw = prefill.startTime ?? null;
    const startValue =
      typeof startValueRaw === 'string' && startValueRaw.trim() === ''
        ? null
        : startValueRaw;
    if (startValue !== undefined) {
      patches.push({
        path: '/start_time',
        op: 'add',
        value: startValue as unknown as string,
      });
    }
  }

  if (prefill.slug !== undefined) {
    const slugValue =
      typeof prefill.slug === 'string' ? prefill.slug.trim() : prefill.slug;
    if (typeof slugValue === 'string' && slugValue.length > 0) {
      patches.push({
        path: '/properties/print_slug',
        op: 'add',
        value: slugValue,
      });
    } else if (slugValue === null || slugValue === '') {
      patches.push({
        path: '/properties/print_slug',
        op: 'add',
        value: null as unknown as string,
      });
    }
  }

  return patches;
};

/**
 * Applies quick launch prefill values to a duplicated asset via PATCH operation.
 * Shared utility for both asset and revision duplication workflows.
 */
export const applyQuickLaunchPrefillPatch = async (
  dataProvider: BloxCustomDataProviderType,
  assetId: string,
  quickLaunchPrefill?: AssetQuickLaunchPrefill,
) => {
  const patchOps = buildQuickLaunchPatch(quickLaunchPrefill);

  if (!patchOps.length) {
    return;
  }

  const result = await dataProvider.patchEditorialAsset({
    assetId: assetId,
    patchList: patchOps,
  });

  return result;
};
